<template>
  <div class="FormulaEditorWrapper" ref="textareaRef"></div>
</template>

<script>
import {
  basicSetup,
  EditorView
} from 'codemirror'
import {
  javascript,
  javascriptLanguage
} from '@codemirror/lang-javascript'
import {
  autocompletion
} from '@codemirror/autocomplete'
import {
  placeholder as codemirrorPlaceholder
} from '@codemirror/view'
import {
  EditorSelection,
  EditorState
} from '@codemirror/state'
import {
  fieldFindRegExp,
  compact
} from './config'
import {
  getFieldNameWidget
} from './fieldNameWidget'
import {
  getFunctionNameWidget
} from './functionNameWidget'
import getTheme from './theme'

export default {
  name: 'FormulaEditor',

  props: {
    value: {
      type: String,
      default: ''
    },
    height: {
      type: Number,
      default: 350
    },
    placeholder: {
      type: String,
      default: '请输入'
    },
    fields: {
      type: Array,
      default: () => []
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    lineWrapping: {
      type: Boolean,
      default: false
    },
    bgColor: {
      type: String,
      default: '#f0f2f5'
    },
    padding: {
      type: Number,
      default: 12
    },
    needJavascript: {
      type: Boolean,
      default: true
    },
    lineActiveColor: {
      type: String,
      default: 'rgba(204,238,255,.27)'
    },
    fieldRegExpText: {
      type: String,
      default: '/^\{\{|\}\}$/g'
    },
    functionKeys: {
      type: Array,
      default: () => []
    },
    functionOptions: {
      type: Array,
      default: () => []
    },
    getCompletionApply: {
      type: String,
      default: ''
    },
    customCompletions: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      myEditorView: null
    }
  },

  watch: {
    value: {
      handler(value) {
        const editorViewInstance = this.myEditorView
        if (editorViewInstance &&
          !editorViewInstance.composing &&
          editorViewInstance.state.doc.toString() !== value) {
          editorViewInstance.dispatch({
            changes: {
              from: 0,
              to: editorViewInstance.state.doc.length,
              insert: value
            },
            selection: EditorSelection.cursor(value.length),
            scrollIntoView: true
          })
        }
      },
      immediate: true
    }
  },

  mounted() {
    this.initEditor()
  },

  beforeDestroy() {
    // 清理编辑器实例
    if (this.myEditorView) {
      this.myEditorView.destroy()
      this.myEditorView = null
    }
  },

  methods: {
    initEditor() {
      const completionFunctionOptions = this.needJavascript ? this.functionOptions : []

      const myCompletions = () => [
        ...this.fields.map((item) => ({
          label: item,
          type: 'definetion',
          apply: this.getCompletionApply?.(item) ?? `{{${item}}}`
        })),
        ...completionFunctionOptions
          .map((item) => [
            ...item.lists.map((func) => ({
              label: func.key,
              detail: func.title,
              type: 'function',
              apply: (view, item, from, to) => {
                this.$emit('checkedFunction', {
                  key: item.label
                })
                let cursorPos = from + item.label.length + 1
                view.dispatch({
                  changes: {
                    from,
                    to,
                    insert: `${item.label}()`
                  },
                  selection: {
                    anchor: cursorPos
                  }
                })
              }
            }))
          ])
          .reduce((prev, next) => prev.concat(next), [])
      ]

      const customCompletions = (context) => {
        if (this.customCompletions) {
          return {
            ...this.customCompletions(context),
            options: myCompletions()
          }
        }
        const Identifier = /^[\w$\xa1-\uffff][\w$\d\xa1-\uffff]*$/
        const matchBeforeIdentifier = /[\w$\xa1-\uffff][\w$\d\xa1-\uffff]*/
        let before = context.matchBefore(matchBeforeIdentifier)
        if (!context.explicit && !before) return null
        return {
          from: before ? before.from : context.pos,
          options: myCompletions(),
          validFor: Identifier
        }
      }

      const domEventListener = EditorView.domEventHandlers({
        click: (e) => {
          if (e?.target?.className === 'cm-custom-function-name') {
            this.$emit('checkedFunction', {
              key: e.target.innerText
            })
          }
        }
      })

      this.myEditorView = new EditorView({
        parent: this.$refs.textareaRef,
        doc: this.value,
        extensions: compact([
          basicSetup,
          domEventListener,
          getFieldNameWidget(this.fieldRegExpText ? new RegExp(this.fieldRegExpText, 'g') : fieldFindRegExp),
          this.needJavascript ? getFunctionNameWidget(this.functionKeys) : undefined,
          this.needJavascript ? javascript() : undefined,
          this.needJavascript
            ? javascriptLanguage.data.of({
                autocomplete: myCompletions()
              })
            : autocompletion({
                override: [customCompletions]
              }),
          EditorView.theme(
            getTheme({
              height: this.height,
              lineActiveColor: this.lineActiveColor,
              bgColor: this.bgColor,
              padding: this.padding
            })
          ),
          this.lineWrapping ? EditorView.lineWrapping : undefined,
          EditorState.readOnly.of(this.readOnly),
          EditorView.editable.of(this.editable),
          this.placeholder ? codemirrorPlaceholder(this.placeholder) : undefined
        ]),
        dispatch: (tr) => {
          this.myEditorView?.update([tr])
          if (!tr.changes.empty) {
            let v = this.myEditorView?.state?.doc?.toString?.() ?? ''
            this.$emit('input', v)
            if (v.trim() === '') {
              this.$emit('clear')
            }
          }
        }
      })
    },

    insertText(text, type = '') {
      const editorViewInstance = this.myEditorView
      if (!editorViewInstance) return
      const replaceSelectionInstance = editorViewInstance.state.replaceSelection(text)
      editorViewInstance.dispatch(replaceSelectionInstance)
      if (type === 'function') {
        const ranges = editorViewInstance.state.selection.ranges
        const editorSelectionInstances = ranges.map((item) =>
          EditorSelection.range(item.anchor - 1, item.anchor - 1)
        )
        editorViewInstance.dispatch({
          selection: EditorSelection.create(editorSelectionInstances, 0)
        })
      }
      editorViewInstance.focus()
    },

    getFoumulaText() {
      return this.myEditorView?.state.doc.toString()
    }
  }
}
</script>